/**
 * Workspace-independent host of the note lane (W2-03 note-lane), registered as an invisible status bar item so the
 * shell mounts it in every editor workspace (the hint bar renders `registry.statusItems('statusbar')` in Chart,
 * Song & Timing and Publish alike):
 *  - renders the package's own dialogs from the lane session (`session.dialog`: insert beats, remove a time range,
 *    rename a marker). `time.insert`, `time.remove` and `markers.*` sit in `timing.context` and the palette, so they
 *    run from the Song & Timing workspace too, where the Chart timeline panel is not mounted;
 *  - forgets the hit of the last timeline right click when a context menu opens anywhere else (a menu opened over
 *    the Song & Timing panels must not act on a stale gutter beat or marker; the timeline's own right clicks set a
 *    fresh hit right after, because this listener runs first, in the capture phase at the document);
 *  - cancels an open dialog when the project changes under it (its answer would act on the wrong chart).
 * Renders nothing else: the dialogs portal to the document body.
 */

import { useEffect } from 'react';
import type { AppServices } from '../../app/registry/types';
import { useSession } from './hooks';
import { LaneDialogs } from './LaneDialogs';
import { laneSession } from './laneSession';

export function LaneShellHost(props: { readonly services: AppServices }) {
  const { services } = props;
  useSession(services);

  useEffect(() => {
    const session = laneSession(services);
    const onContextMenu = (): void => {
      session.context = null;
      session.gutter = null;
    };
    document.addEventListener('contextmenu', onContextMenu, true);
    return () => {
      document.removeEventListener('contextmenu', onContextMenu, true);
    };
  }, [services]);

  useEffect(() => {
    const session = laneSession(services);
    let store = services.store;
    return services.onChange(() => {
      if (services.store === store) return;
      store = services.store;
      session.context = null;
      session.gutter = null;
      session.dialog?.resolve(null);
    });
  }, [services]);

  return <LaneDialogs services={services} />;
}
